import { useEffect } from "react";
import { useLocation } from "wouter";
import { getRouteKeyFromPath, getLocalizedPath } from "@/hooks/useLocalizedPath";
import blogData from "../../../blog-articles-data.json";

const BASE_URL = "https://skinbyraquel.com";

/**
 * Injects <link rel="canonical"> and <link rel="alternate" hreflang="..."> tags
 * into the document head based on the current URL path.
 * 
 * Strategy:
 * - The Spanish (root) version is always the canonical URL
 * - hreflang alternates point to localized slugs for ES, EN, and SV
 * - x-default points to the Spanish version
 * - Blog articles use their localized slug_es/slug_sv fields
 */
export default function CanonicalHead() {
  const [location] = useLocation();

  useEffect(() => {
    let esUrl: string;
    let enUrl: string;
    let svUrl: string;

    // Check if this is a blog article page
    const blogMatch = location.match(/^(?:\/(en|sv))?\/(?:blog|blogg)\/(.+)$/);
    if (blogMatch) {
      const slug = blogMatch[2];
      // Find the article by any of its slugs
      const articles = blogData as any[];
      const article = articles.find((a: any) =>
        a.slug === slug || a.slug_es === slug || a.slug_sv === slug
      );
      if (article) {
        esUrl = `${BASE_URL}/blog/${article.slug_es || article.slug}`;
        enUrl = `${BASE_URL}/en/blog/${article.slug}`;
        svUrl = `${BASE_URL}/sv/blogg/${article.slug_sv || article.slug}`;
      } else {
        // Fallback if article not found
        esUrl = `${BASE_URL}/blog/${slug}`;
        enUrl = `${BASE_URL}/en/blog/${slug}`;
        svUrl = `${BASE_URL}/sv/blogg/${slug}`;
      }
    } else {
      // Try to find the route key from the current path
      const result = getRouteKeyFromPath(location);

      if (result) {
        // Use the localized slug map for proper URLs
        const esPath = getLocalizedPath(result.routeKey, 'es');
        const enPath = getLocalizedPath(result.routeKey, 'en');
        const svPath = getLocalizedPath(result.routeKey, 'sv');

        esUrl = `${BASE_URL}${esPath === '/' ? '' : esPath}`;
        enUrl = `${BASE_URL}${enPath}`;
        svUrl = `${BASE_URL}${svPath}`;
      } else {
        // Fallback: strip prefix and add language prefix
        let basePath = location;
        if (basePath.startsWith("/en")) {
          basePath = basePath.slice(3) || "/";
        } else if (basePath.startsWith("/sv")) {
          basePath = basePath.slice(3) || "/";
        }
        if (!basePath.startsWith("/")) {
          basePath = "/" + basePath;
        }

        esUrl = `${BASE_URL}${basePath === "/" ? "" : basePath}`;
        enUrl = `${BASE_URL}/en${basePath === "/" ? "" : basePath}`;
        svUrl = `${BASE_URL}/sv${basePath === "/" ? "" : basePath}`;
      }
    }

    const canonicalUrl = esUrl;

    // Remove existing canonical and hreflang tags
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) existingCanonical.remove();
    const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflangs.forEach((el) => el.remove());

    // Add canonical
    const canonicalLink = document.createElement("link");
    canonicalLink.rel = "canonical";
    canonicalLink.href = canonicalUrl;
    document.head.appendChild(canonicalLink);

    // Add hreflang alternates
    const hreflangs = [
      { lang: "es", url: esUrl },
      { lang: "en", url: enUrl },
      { lang: "sv", url: svUrl },
      { lang: "x-default", url: esUrl },
    ];
    hreflangs.forEach(({ lang, url }) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lang;
      link.href = url;
      document.head.appendChild(link);
    });

    // Cleanup on unmount or location change
    return () => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      const alternates = document.querySelectorAll('link[rel="alternate"][hreflang]');
      alternates.forEach((el) => el.remove());
    };
  }, [location]);

  return null;
}
